import * as React from 'react';
import { Button as ShadcnButton } from '../ui/button';
import { cn } from '../../lib/utils';

/* ────────────────────────────────────────────────────────────────────────────
   v9 button wrappers. These build ON TOP of shadcn's Button so we get its
   focus-visible ring, disabled state, and Slot/asChild plumbing for free.

   Differences from shadcn defaults:
     - Padding: py-3 (v9 standard) vs shadcn's py-2
     - Rounding: rounded-[10px] (v9 0.625rem) vs shadcn's rounded-md
     - Hover colours: explicit (slate-800 / slate-50) so they read clearly

   variant in v9 terms:
     - HPrimaryButton  ≈ .h-btn-primary   (slate-900 fill, white text)
     - HSecondaryButton ≈ .h-btn-secondary (white, slate-200 border)
     - HGhostButton    ≈ .h-btn-ghost     (transparent, slate-500 text)
     - HDestructiveButton (red 600 fill) — used in factory-reset confirm
   ──────────────────────────────────────────────────────────────────────────── */

// v9 design-system button. Fixed 38px minimum height so the button reads
// substantial but not heavy; padding + text size don't perturb it. Inputs
// use the same min-h so input+button rows line up.
const baseSize = 'min-h-[38px] py-2 px-4 rounded-[10px] text-[14px] font-medium';

export const HPrimaryButton = React.forwardRef(function HPrimaryButton(
  { className, ...rest },
  ref,
) {
  return (
    <ShadcnButton
      ref={ref}
      variant="default"
      className={cn(
        baseSize,
        'bg-slate-900 text-white hover:bg-slate-800 active:bg-slate-950',
        'shadow-none',
        className,
      )}
      {...rest}
    />
  );
});

export const HSecondaryButton = React.forwardRef(function HSecondaryButton(
  { className, ...rest },
  ref,
) {
  return (
    <ShadcnButton
      ref={ref}
      variant="outline"
      className={cn(
        baseSize,
        'bg-white text-slate-700 border border-slate-200 hover:bg-slate-50 hover:text-slate-900 active:bg-slate-100',
        className,
      )}
      {...rest}
    />
  );
});

export const HGhostButton = React.forwardRef(function HGhostButton(
  { className, ...rest },
  ref,
) {
  return (
    <ShadcnButton
      ref={ref}
      variant="ghost"
      className={cn(
        'h-auto py-2 px-2 text-[13px] font-medium text-slate-500 hover:bg-slate-100 hover:text-slate-700 rounded-md',
        className,
      )}
      {...rest}
    />
  );
});

export const HDestructiveButton = React.forwardRef(function HDestructiveButton(
  { className, ...rest },
  ref,
) {
  return (
    <ShadcnButton
      ref={ref}
      variant="default"
      className={cn(
        baseSize,
        'bg-red-600 text-white hover:bg-red-700 active:bg-red-800',
        className,
      )}
      {...rest}
    />
  );
});

/* Pill button used for the "+ New Higlo" header action in v9 5.1. */
export const HPillButton = React.forwardRef(function HPillButton(
  { className, ...rest },
  ref,
) {
  return (
    <ShadcnButton
      ref={ref}
      variant="default"
      className={cn(
        'h-9 px-3 rounded-full text-[12px] font-semibold bg-slate-900 text-white hover:bg-slate-800',
        className,
      )}
      {...rest}
    />
  );
});

/* Small circular icon button (back arrow, close X, etc.). v9 .h-icon-btn. */
export const HIconButton = React.forwardRef(function HIconButton(
  { className, ...rest },
  ref,
) {
  return (
    <ShadcnButton
      ref={ref}
      variant="outline"
      className={cn(
        'h-9 w-9 p-0 rounded-full bg-white text-slate-600 border border-slate-200 hover:bg-slate-50 hover:text-slate-900',
        className,
      )}
      {...rest}
    />
  );
});

/* Equal-width Back / Continue (or Cancel / Save) footer pair.
   Pass two children — usually a Secondary then a Primary. */
export function HButtonPair({ className, children, ...rest }) {
  return (
    <div className={cn('grid grid-cols-2 gap-2', className)} {...rest}>
      {children}
    </div>
  );
}
